import { MessageSquareText } from 'lucide-react';

import type { SSEMessage } from '@/_shared/types';

import { MessageInput } from '../../chat/components/MessageInput';
import { MessageList } from '../../chat/components/MessageList';
import { generateSuggestedPrompts } from '../lib/dashboard-prompts';
import type { Dashboard } from '../types';

interface DashboardChatSidebarProps {
  dashboard: Dashboard;
  paramValues: Record<string, string | string[]>;
  messages: SSEMessage[];
  agentActive: boolean;
  /** True when a confirmed turn ID is set — gates the Stop button. */
  canStop?: boolean;
  /** Disable input until session is ready. */
  disabled?: boolean;
  onSend: (content: string) => void;
  onReset: () => void;
  onClose: () => void;
  onStop?: () => void;
  prefillValue?: string;
  prefillNonce?: number;
}

function EmptyState({
  dashboard,
  disabled,
  onSend,
}: {
  dashboard: Dashboard;
  disabled: boolean;
  onSend: (content: string) => void;
}): React.ReactElement {
  const prompts = generateSuggestedPrompts(dashboard);
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
        <MessageSquareText size={20} />
      </div>
      <p className="text-sm font-medium text-foreground">Ask about this dashboard</p>
      <p className="mt-1 text-center text-xs text-muted-foreground">Analyze data, explain trends, and find anomalies</p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {prompts.map((p, i) => (
          <button
            key={i}
            onClick={() => onSend(p.prompt)}
            disabled={disabled}
            className="rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function DashboardChatSidebar({
  dashboard,
  messages,
  agentActive,
  canStop = false,
  disabled = false,
  onSend,
  onStop,
  prefillValue,
  prefillNonce,
}: DashboardChatSidebarProps): React.ReactElement {
  const hasMessages = messages.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Messages or empty state */}
      {hasMessages ? (
        <MessageList
          messages={messages}
          thinking={agentActive}
          onAction={onSend}
          compact
          actionsDisabled={agentActive}
        />
      ) : (
        <EmptyState dashboard={dashboard} disabled={disabled} onSend={onSend} />
      )}

      {/* Input */}
      <MessageInput
        onSend={onSend}
        disabled={agentActive || disabled}
        variant="sidebar"
        onStop={canStop ? onStop : undefined}
        prefillValue={prefillValue}
        prefillNonce={prefillNonce}
      />
    </div>
  );
}
